import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './styles/ConnectProfile.css';

export default function ConnectProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Profile data (extended from registration)
  const [profileData, setProfileData] = useState({
    // From registration
    fullName: '',
    gender: '',
    birthDate: '',
    email: '',
    countryOfResidence: '',
    currentLocation: '',
    // Onboarding fields
    pronouns: '',
    genderIdentity: '',
    sexualOrientation: '',
    visibility: 'public',
    bio: '',
    travelStyle: '',
    favoriteDestinations: [],
    // Profile extras
    profilePicture: '',
    coverPhoto: ''
  });

  const [destinationInput, setDestinationInput] = useState('');
  const [activeTab, setActiveTab] = useState('posts');

  // Mock posts data
  const [posts] = useState([
    {
      id: 1,
      content: 'Just arrived in Curaçao! The colors of Willemstad are absolutely breathtaking. Can\'t wait to explore more of this beautiful island! 🌈🏝️',
      image: 'https://images.unsplash.com/photo-1568402102990-bc541580b59f?w=800',
      likes: 47,
      comments: 12,
      timestamp: Date.now() - 1000 * 60 * 60 * 2,
      location: 'Willemstad, Curaçao'
    },
    {
      id: 2,
      content: 'Found the most amazing LGBTQ+ friendly beach bar in Aruba! The staff was so welcoming and the sunset views are incredible. Highly recommend! 🌅',
      image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800',
      likes: 89,
      comments: 23,
      timestamp: Date.now() - 1000 * 60 * 60 * 24 * 2,
      location: 'Palm Beach, Aruba'
    },
    {
      id: 3,
      content: 'Meeting fellow travelers from the community has been the highlight of this trip. The connections we make while traveling are so special. 💜',
      image: null,
      likes: 34,
      comments: 8,
      timestamp: Date.now() - 1000 * 60 * 60 * 24 * 5,
      location: 'San Juan, Puerto Rico'
    },
    {
      id: 4,
      content: 'Pro tip: The local markets in Barbados are a must-visit! Got some amazing handmade jewelry and the vendors are so friendly. Supporting local businesses feels great! 🛍️',
      image: 'https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=800',
      likes: 56,
      comments: 15,
      timestamp: Date.now() - 1000 * 60 * 60 * 24 * 7,
      location: 'Bridgetown, Barbados'
    }
  ]);

  // Options
  const pronounOptions = [
    'he/him', 'she/her', 'they/them', 'he/they', 'she/they',
    'ze/zir', 'xe/xem', 'Any pronouns', 'Prefer not to say', 'Custom'
  ];

  const genderIdentityOptions = [
    'Cisgender Man', 'Cisgender Woman', 'Transgender Man', 'Transgender Woman',
    'Non-binary', 'Genderqueer', 'Genderfluid', 'Agender', 'Bigender',
    'Two-Spirit', 'Questioning', 'Prefer not to say', 'Other'
  ];

  const orientationOptions = [
    'Gay', 'Lesbian', 'Bisexual', 'Pansexual', 'Queer', 'Asexual',
    'Demisexual', 'Straight Ally', 'Questioning', 'Prefer not to say', 'Other'
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Public', desc: 'Anyone can see your profile' },
    { value: 'community', label: 'Community Only', desc: 'Only verified members can see' },
    { value: 'private', label: 'Private', desc: 'Only you can see your profile' }
  ];

  const travelStyleOptions = [
    'Solo Adventurer', 'Couple Travel', 'Group Explorer', 'Luxury Seeker',
    'Budget Backpacker', 'Cultural Immersion', 'Beach & Relaxation',
    'Nightlife & Parties', 'Nature & Outdoors', 'Foodie Journey'
  ];

  const suggestedDestinations = [
    'Puerto Rico', 'Curaçao', 'Aruba', 'Jamaica', 'Barbados',
    'Dominican Republic', 'St. Lucia', 'Bahamas', 'Trinidad', 'Martinique'
  ];

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('connectUser');
    if (!storedUser) {
      navigate('/connect');
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);

    // Populate profile data from stored user
    setProfileData(prev => ({
      ...prev,
      fullName: userData.fullName || '',
      gender: userData.gender || '',
      birthDate: userData.birthDate || '',
      email: userData.email || '',
      countryOfResidence: userData.countryOfResidence || '',
      currentLocation: userData.currentLocation || '',
      pronouns: userData.pronouns || '',
      genderIdentity: userData.genderIdentity || '',
      sexualOrientation: userData.sexualOrientation || '',
      visibility: userData.visibility || 'public',
      bio: userData.bio || '',
      travelStyle: userData.travelStyle || '',
      favoriteDestinations: userData.favoriteDestinations || []
    }));

    // Show onboarding if not completed
    if (!userData.onboardingComplete) {
      setShowOnboarding(true);
    }
  }, [navigate]);

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const addDestination = (dest) => {
    if (dest && !profileData.favoriteDestinations.includes(dest)) {
      setProfileData(prev => ({
        ...prev,
        favoriteDestinations: [...prev.favoriteDestinations, dest]
      }));
    }
    setDestinationInput('');
  };

  const removeDestination = (dest) => {
    setProfileData(prev => ({
      ...prev,
      favoriteDestinations: prev.favoriteDestinations.filter(d => d !== dest)
    }));
  };

  const handleOnboardingNext = () => {
    if (onboardingStep < 3) {
      setOnboardingStep(onboardingStep + 1);
    } else {
      // Complete onboarding
      const updatedUser = {
        ...user,
        ...profileData,
        onboardingComplete: true
      };
      localStorage.setItem('connectUser', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setShowOnboarding(false);
    }
  };

  const handleOnboardingSkip = () => {
    const updatedUser = {
      ...user,
      onboardingComplete: true
    };
    localStorage.setItem('connectUser', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setShowOnboarding(false);
  };

  const handleSaveProfile = () => {
    const updatedUser = {
      ...user,
      ...profileData
    };
    localStorage.setItem('connectUser', JSON.stringify(updatedUser));
    setUser(updatedUser);
    setIsEditing(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('connectUser');
    navigate('/connect');
  };

  if (!user) {
    return null;
  }

  // Onboarding Modal
  const renderOnboarding = () => (
    <div className="connect-onboarding-overlay">
      <div className="connect-onboarding-modal">
        <div className="connect-onboarding-header">
          <h2>Complete Your Profile</h2>
          <p>Help us personalize your experience (optional but encouraged)</p>
          <div className="connect-onboarding-progress">
            <div className="connect-progress-bar">
              <div 
                className="connect-progress-fill" 
                style={{ width: `${(onboardingStep / 3) * 100}%` }}
              ></div>
            </div>
            <span className="connect-progress-text">Step {onboardingStep} of 3</span>
          </div>
        </div>

        <div className="connect-onboarding-content">
          {/* Step 1: Identity */}
          {onboardingStep === 1 && (
            <div className="connect-onboarding-step">
              <h3>Your Identity</h3>
              <p className="connect-step-desc">
                Share as much or as little as you're comfortable with
              </p>

              <div className="connect-onboard-field">
                <label>Pronouns</label>
                <select
                  value={profileData.pronouns}
                  onChange={(e) => handleProfileChange('pronouns', e.target.value)}
                  className="connect-onboard-select"
                >
                  <option value="">Select pronouns</option>
                  {pronounOptions.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div className="connect-onboard-field">
                <label>Gender Identity</label>
                <select
                  value={profileData.genderIdentity}
                  onChange={(e) => handleProfileChange('genderIdentity', e.target.value)}
                  className="connect-onboard-select"
                >
                  <option value="">Select gender identity</option>
                  {genderIdentityOptions.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="connect-onboard-field">
                <label>Sexual Orientation</label>
                <select
                  value={profileData.sexualOrientation}
                  onChange={(e) => handleProfileChange('sexualOrientation', e.target.value)}
                  className="connect-onboard-select"
                >
                  <option value="">Select orientation</option>
                  {orientationOptions.map(o => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Visibility & Bio */}
          {onboardingStep === 2 && (
            <div className="connect-onboarding-step">
              <h3>About You</h3>
              <p className="connect-step-desc">
                Tell the community a bit about yourself
              </p>

              <div className="connect-onboard-field">
                <label>Profile Visibility</label>
                <div className="connect-visibility-options">
                  {visibilityOptions.map(opt => (
                    <div
                      key={opt.value}
                      className={`connect-visibility-option ${profileData.visibility === opt.value ? 'selected' : ''}`}
                      onClick={() => handleProfileChange('visibility', opt.value)}
                    >
                      <div className="connect-visibility-radio">
                        {profileData.visibility === opt.value && <div className="connect-radio-dot"></div>}
                      </div>
                      <div className="connect-visibility-info">
                        <span className="connect-visibility-label">{opt.label}</span>
                        <span className="connect-visibility-desc">{opt.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="connect-onboard-field">
                <label>Bio</label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  className="connect-onboard-textarea"
                  placeholder="Tell us about yourself, your interests, what you're looking for..."
                  rows={4}
                  maxLength={500}
                />
                <span className="connect-char-count">{profileData.bio.length}/500</span>
              </div>
            </div>
          )}

          {/* Step 3: Travel Preferences */}
          {onboardingStep === 3 && (
            <div className="connect-onboarding-step">
              <h3>Travel Preferences</h3>
              <p className="connect-step-desc">
                Help us connect you with like-minded travelers
              </p>

              <div className="connect-onboard-field">
                <label>Travel Style</label>
                <div className="connect-travel-styles">
                  {travelStyleOptions.map(style => (
                    <button
                      key={style}
                      type="button"
                      className={`connect-travel-style-btn ${profileData.travelStyle === style ? 'selected' : ''}`}
                      onClick={() => handleProfileChange('travelStyle', style)}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              <div className="connect-onboard-field">
                <label>Favorite Destinations</label>
                <div className="connect-destinations-input">
                  <input
                    type="text"
                    value={destinationInput}
                    onChange={(e) => setDestinationInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addDestination(destinationInput)}
                    placeholder="Type a destination and press Enter"
                    className="connect-onboard-input"
                  />
                </div>
                <div className="connect-suggested-destinations">
                  {suggestedDestinations
                    .filter(d => !profileData.favoriteDestinations.includes(d))
                    .slice(0, 5)
                    .map(dest => (
                      <button
                        key={dest}
                        type="button"
                        className="connect-suggested-btn"
                        onClick={() => addDestination(dest)}
                      >
                        + {dest}
                      </button>
                    ))}
                </div>
                {profileData.favoriteDestinations.length > 0 && (
                  <div className="connect-selected-destinations">
                    {profileData.favoriteDestinations.map(dest => (
                      <span key={dest} className="connect-destination-tag">
                        {dest}
                        <button onClick={() => removeDestination(dest)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="connect-onboarding-actions">
          <button
            type="button"
            className="connect-onboard-skip"
            onClick={handleOnboardingSkip}
          >
            Skip for now
          </button>
          <div className="connect-onboard-nav">
            {onboardingStep > 1 && (
              <button
                type="button"
                className="connect-onboard-back"
                onClick={() => setOnboardingStep(onboardingStep - 1)}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="connect-onboard-next"
              onClick={handleOnboardingNext}
            >
              {onboardingStep === 3 ? 'Complete' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="connect-profile-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <nav className="connect-profile-nav">
          <Link to="/connect/profile" className="connect-nav-link active">Profile</Link>
          <Link to="/connect/discover" className="connect-nav-link">Discover</Link>
          <Link to="/connect/messages" className="connect-nav-link">Messages</Link>
        </nav>
        <button className="connect-logout-btn" onClick={handleLogout}>
          Log Out
        </button>
        <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <Link to="/connect/profile" className="connect-nav-link active" onClick={() => setShowMobileNav(false)}>Profile</Link>
              <Link to="/connect/discover" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Discover</Link>
              <Link to="/connect/messages" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Messages</Link>
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      {/* Profile Content */}
      <main className="connect-profile-main">
        {/* Cover & Avatar */}
        <div className="connect-profile-cover">
          <div className="connect-cover-image">
            {profileData.coverPhoto ? (
              <img src={profileData.coverPhoto} alt="Cover" />
            ) : (
              <div className="connect-cover-placeholder"></div>
            )}
          </div>
          <div className="connect-profile-avatar">
            {profileData.profilePicture ? (
              <img src={profileData.profilePicture} alt="Profile" />
            ) : (
              <div className="connect-avatar-placeholder">
                {profileData.fullName ? profileData.fullName.charAt(0).toUpperCase() : '?'}
              </div>
            )}
          </div>
        </div>

        {/* Profile Info */}
        <div className="connect-profile-info">
          <div className="connect-profile-name-section">
            <h1 className="connect-profile-name">{profileData.fullName || 'Your Name'}</h1>
            {profileData.pronouns && (
              <span className="connect-profile-pronouns">{profileData.pronouns}</span>
            )}
          </div>

          {profileData.currentLocation && (
            <p className="connect-profile-location">📍 {profileData.currentLocation}</p>
          )}

          {/* Identity Tags */}
          <div className="connect-profile-tags">
            {profileData.genderIdentity && (
              <span className="connect-profile-tag">{profileData.genderIdentity}</span>
            )}
            {profileData.sexualOrientation && (
              <span className="connect-profile-tag">{profileData.sexualOrientation}</span>
            )}
            {profileData.travelStyle && (
              <span className="connect-profile-tag travel">{profileData.travelStyle}</span>
            )}
          </div>

          {profileData.bio && (
            <p className="connect-profile-bio">{profileData.bio}</p>
          )}

          {/* Favorite Destinations */}
          {profileData.favoriteDestinations.length > 0 && (
            <div className="connect-profile-destinations">
              <h3>Favorite Destinations</h3>
              <div className="connect-destinations-list">
                {profileData.favoriteDestinations.map(dest => (
                  <span key={dest} className="connect-dest-chip">{dest}</span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="connect-profile-actions">
            <button
              className="connect-edit-profile-btn"
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </button>
            <button
              className="connect-onboarding-btn"
              onClick={() => { setOnboardingStep(1); setShowOnboarding(true); }}
            >
              Update Preferences
            </button>
          </div>

          {/* Profile Completion */}
          {!user.onboardingComplete && (
            <div className="connect-profile-incomplete">
              <p>🌈 Complete your profile to connect with more travelers!</p>
              <button onClick={() => setShowOnboarding(true)}>Complete Now</button>
            </div>
          )}
        </div>

        {/* Posts Section */}
        <div className="connect-profile-content">
          <div className="connect-content-tabs">
            <button 
              className={`connect-content-tab ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              Posts ({posts.length})
            </button>
            <button 
              className={`connect-content-tab ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
            >
              Photos
            </button>
            <button 
              className={`connect-content-tab ${activeTab === 'trips' ? 'active' : ''}`}
              onClick={() => setActiveTab('trips')}
            >
              Trips
            </button>
          </div>

          {activeTab === 'posts' && (
            <div className="connect-posts-list">
              {posts.map(post => (
                <div key={post.id} className="connect-post-card">
                  <div className="connect-post-header">
                    <div className="connect-post-avatar">
                      {profileData.profilePicture ? (
                        <img src={profileData.profilePicture} alt="Profile" />
                      ) : (
                        <div className="connect-post-avatar-placeholder">
                          {profileData.fullName ? profileData.fullName.charAt(0).toUpperCase() : '?'}
                        </div>
                      )}
                    </div>
                    <div className="connect-post-meta">
                      <span className="connect-post-author">{profileData.fullName || 'You'}</span>
                      <span className="connect-post-time">
                        {formatTimeAgo(post.timestamp)}
                        {post.location && ` · 📍 ${post.location}`}
                      </span>
                    </div>
                  </div>
                  <p className="connect-post-content">{post.content}</p>
                  {post.image && (
                    <div className="connect-post-image">
                      <img src={post.image} alt="Post" />
                    </div>
                  )}
                  <div className="connect-post-actions">
                    <button className="connect-post-action">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                      <span>{post.likes}</span>
                    </button>
                    <button className="connect-post-action">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <span>{post.comments}</span>
                    </button>
                    <button className="connect-post-action">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3"/>
                        <circle cx="6" cy="12" r="3"/>
                        <circle cx="18" cy="19" r="3"/>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                      </svg>
                      <span>Share</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'photos' && (
            <div className="connect-photos-grid">
              {posts.filter(p => p.image).map(post => (
                <div key={post.id} className="connect-photo-item">
                  <img src={post.image} alt="Photo" />
                </div>
              ))}
            </div>
          )}

          {activeTab === 'trips' && (
            <div className="connect-trips-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="10" r="3"/>
                <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/>
              </svg>
              <h3>No trips yet</h3>
              <p>Share your travel experiences with the community</p>
            </div>
          )}
        </div>
      </main>

      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="connect-edit-overlay">
          <div className="connect-edit-modal">
            <div className="connect-edit-header">
              <h2>Edit Profile</h2>
              <button className="connect-edit-close" onClick={() => setIsEditing(false)}>×</button>
            </div>
            <div className="connect-edit-content">
              <div className="connect-edit-field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => handleProfileChange('fullName', e.target.value)}
                />
              </div>
              <div className="connect-edit-field">
                <label>Current Location</label>
                <input
                  type="text"
                  value={profileData.currentLocation}
                  onChange={(e) => handleProfileChange('currentLocation', e.target.value)}
                />
              </div>
              <div className="connect-edit-field">
                <label>Bio</label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className="connect-edit-actions">
              <button className="connect-edit-cancel" onClick={() => setIsEditing(false)}>Cancel</button>
              <button className="connect-edit-save" onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && renderOnboarding()}
    </div>
  );
}
